import type { NetworkNode } from "@xop-any-time/shared";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, CornerDownRight } from "lucide-react";
import { useState, type FormEvent, type JSX } from "react";

import { ApiError } from "../../lib/api.js";
import { PageHeader } from "../portal/crm.js";
import { accountKeys, createChild, getNetwork } from "./api.js";

/**
 * Your reseller network — who you sit under, and who sits under you.
 *
 * THERE IS NO DEPTH LIMIT. A reseller can have a reseller, which can have a
 * reseller, for as long as the business needs it (spec D §3). What used to cap
 * this at five levels was a constant doing three jobs; only the termination
 * guarantees survived, and the subtree query is now cycle-proof by
 * construction rather than by counting.
 *
 * WHAT YOU SEE OF EACH DIRECTION IS NOT SYMMETRIC, on purpose:
 *
 *   • **Downward** — your descendants, with their customer and people counts.
 *     Their customers and invoices are already yours to see; that is what the
 *     hierarchy means.
 *   • **Upward** — your ancestors, as NAMES ONLY. A label is not access: you
 *     cannot see a single row belonging to the company above you, and every
 *     by-id endpoint in the CRM is still scoped to your own subtree. It is
 *     here because who you sit under is a fact you already know from your
 *     contract, and rendering "no parent" on a reseller that has one would be
 *     a lie an operator would act on.
 */
export function NetworkPage(): JSX.Element {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: accountKeys.network,
    queryFn: getNetwork,
  });
  const [toast, setToast] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    const revoked = error instanceof ApiError && error.status === 403;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader title="Reseller network" />
        <Alert severity={revoked ? "warning" : "error"}>
          {revoked
            ? "Your access was removed. Contact your XOP Networks channel manager."
            : "We could not load your network just now. Refresh the page to try again."}
        </Alert>
      </Box>
    );
  }

  const isOwner = data.viewer.role === "owner";
  const children = data.descendants.filter((d) => d.parentResellerId === data.reseller.id);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Reseller network"
        description={
          data.depth === 0
            ? "Your company sells directly for XOP Networks."
            : `Your company sits ${data.depth} ${data.depth === 1 ? "level" : "levels"} under XOP Networks.`
        }
      />

      {/* ── Upward: labels only ─────────────────────────────────────── */}
      {data.ancestors.length > 0 && (
        <Card>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>You sell through</Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              {[...data.ancestors].reverse().map((a) => (
                <Chip key={a.id} label={a.name} size="small" variant="outlined" />
              ))}
              <CornerDownRight size={14} />
              <Chip label={data.reseller.name} size="small" color="primary" />
            </Box>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              You can see who you sell through, but none of their customers or documents.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* ── Your own company ────────────────────────────────────────── */}
      <Card>
        <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            <Building2 size={20} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontWeight: 600 }}>{data.reseller.name}</Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              {/* The invoice-numbering prefix. Worth showing: it is the first
                  segment of every document number this company issues. */}
              {data.reseller.code
                ? `Document numbers start ${data.reseller.code}-`
                : "No numbering prefix set"}
            </Typography>
          </Box>
          <Counts node={data.reseller} />
        </CardContent>
      </Card>

      {isOwner && <AddChildCard onDone={(m) => setToast(m)} />}

      {/* ── Downward ────────────────────────────────────────────────── */}
      <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography sx={{ fontWeight: 600 }}>
            Below you {data.descendants.length > 0 && `(${data.descendants.length})`}
          </Typography>

          {data.descendants.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              {isOwner
                ? "No sub-resellers yet. Add one above and their customers and invoices will appear in your figures."
                : "No sub-resellers yet."}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {children.map((child) => (
                <Branch
                  key={child.id}
                  node={child}
                  all={data.descendants}
                  depth={0}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={toast !== null}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        message={toast ?? ""}
      />
    </Box>
  );
}

/**
 * One node and everything under it.
 *
 * Recursive with NO depth guard, matching the server: the list it walks is a
 * finite array crm-api already built from a cycle-proof query, so the only way
 * this could recurse forever is a cycle the CTE cannot produce. Indentation is
 * capped for LAYOUT — a tenth-level row would otherwise start off-screen —
 * which is a visual concern and not a limit on the data.
 */
function Branch({
  node,
  all,
  depth,
}: {
  node: NetworkNode;
  all: NetworkNode[];
  depth: number;
}): JSX.Element {
  const kids = all.filter((d) => d.parentResellerId === node.id);
  const indent = Math.min(depth, 6) * 2.5;

  return (
    <>
      <Box
        data-testid="network-node"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          pl: indent,
          py: 0.75,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {depth > 0 && (
          <Box sx={{ display: "flex", color: "text.disabled" }}>
            <CornerDownRight size={14} />
          </Box>
        )}
        <Typography sx={{ fontSize: 14, fontWeight: 500, flex: 1, minWidth: 140 }}>
          {node.name}
        </Typography>
        {node.code && (
          <Chip
            label={node.code}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontVariantNumeric: "tabular-nums" }}
          />
        )}
        <Counts node={node} />
      </Box>
      {kids.map((kid) => (
        <Branch key={kid.id} node={kid} all={all} depth={depth + 1} />
      ))}
    </>
  );
}

function Counts({ node }: { node: NetworkNode }): JSX.Element {
  return (
    <Typography
      sx={{ fontSize: 12, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}
    >
      {node.customerCount ?? 0} customers · {node.memberCount ?? 0} people
    </Typography>
  );
}

function AddChildCard({ onDone }: { onDone: (message: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");

  const create = useMutation({
    mutationFn: () => createChild({ name, ownerEmail }),
    onSuccess: (res) => {
      setName("");
      setOwnerEmail("");
      onDone(
        res.invited
          ? `${res.reseller.name} created. We invited ${ownerEmail} to set a password.`
          : `${res.reseller.name} created, but the invitation email could not be sent. Ask your XOP Networks channel manager to resend it.`,
      );
      void qc.invalidateQueries({ queryKey: accountKeys.network });
    },
  });

  return (
    <Card>
      <CardContent
        component="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (name.trim() !== "" && ownerEmail.trim() !== "") create.mutate();
        }}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Typography sx={{ fontWeight: 600 }}>Add a sub-reseller</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          A company that sells through you. Their customers and invoices count towards your
          figures, they bill under their own name, and their owner manages their own team.
        </Typography>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <TextField
            label="Company name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ flex: "1 1 220px" }}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="Owner's email"
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            sx={{ flex: "1 1 240px" }}
            inputProps={{ maxLength: 255 }}
            /* The company is unusable without one, so it is asked for at
               creation rather than left as a second step someone forgets. */
            helperText="They will own the new company and can invite their own people."
          />
          <Button
            type="submit"
            variant="contained"
            disabled={create.isPending || name.trim() === "" || ownerEmail.trim() === ""}
            sx={{ mt: 1 }}
          >
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </Box>

        {create.isError && (
          <Alert severity="error">
            {create.error instanceof ApiError
              ? create.error.message
              : "We could not create that company."}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
